import { useEffect, useRef, useState } from "react";
import { Music, Repeat, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const SPEEDS = [0.75, 1, 1.25, 1.5];

// Convert a YouTube watch/short URL to an embeddable URL (loop-aware).
function ytEmbed(url, loop, muted) {
  const m = String(url).match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/);
  if (!m) return null;
  const id = m[1];
  const l = loop ? `&loop=1&playlist=${id}` : "";
  const mu = muted ? "&mute=1" : "";
  return `https://www.youtube.com/embed/${id}?rel=0${l}${mu}`;
}

// Admin-managed chanting audio on the Japa page. Tracks are curated_audios with
// show_on_japa=true (YouTube link or uploaded file). Listeners get playback-speed
// control for uploaded audio; the track loops if the admin enabled Loop.
export default function JapaAudio() {
  const { pick } = useLang();
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/audios?placement=japa`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTracks(Array.isArray(d) ? d.filter((t) => t.url) : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, idx]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted, idx]);

  if (!tracks.length) return null;
  const t = tracks[Math.min(idx, tracks.length - 1)];
  const embed = t.kind === "youtube" ? ytEmbed(t.url, t.loop, muted) : null;

  return (
    <section data-testid="japa-audio" className="pan-card pan-accent mx-auto mt-10 w-full max-w-md p-5">
      <div className="flex items-center gap-2">
        <Music className="h-4 w-4 text-saffron" />
        <h3 className="overline-label text-ink-3">{pick("जप ध्वनि", "Chant along")}</h3>
        {t.loop && <span className="inline-flex items-center gap-1 text-[11px] text-ink-3"><Repeat className="h-3 w-3" /> {pick("लूप", "Loop")}</span>}
        <button type="button" data-testid="japa-audio-mute" onClick={() => setMuted((m) => !m)} aria-pressed={muted}
          title={muted ? pick("ध्वनि चालू करें", "Unmute") : pick("मौन करें", "Mute")}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[11px] text-ink-3 transition-colors hover:text-saffron">
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          {muted ? pick("मौन", "Muted") : pick("ध्वनि", "Sound")}
        </button>
      </div>

      <p className="mt-2 text-[17px] font-medium text-ink">{t.title || pick("जप ध्वनि", "Chant audio")}</p>

      {tracks.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tracks.map((x, i) => (
            <button key={x.id} data-testid={`japa-audio-track-${i}`} onClick={() => setIdx(i)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${i === idx ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2 hover:border-ink-3"}`}>
              {x.title || `Track ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {embed ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-line">
          <iframe title={t.title || "Japa audio"} src={embed} className="aspect-video w-full" frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : (
        <>
          <audio key={t.id} ref={audioRef} data-testid="japa-audio-el" src={t.url} controls loop={!!t.loop}
            className="mt-4 w-full" onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = speed; }} />
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-ink-3">{pick("गति", "Speed")}</span>
            {SPEEDS.map((s) => (
              <button key={s} data-testid={`japa-audio-speed-${s}`} onClick={() => setSpeed(s)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${speed === s ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2 hover:border-ink-3"}`}>
                {s}×
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
